import { marked } from 'marked';
import DOMPurify from 'dompurify';

// Pick a readable text color (near-black or white) for an admin-chosen
// background. Once a solid background is set the card no longer follows the
// theme, so its text must contrast with that fixed color rather than the
// light/dark theme. Uses the YIQ perceived-brightness approximation.
function readableTextColor(hex) {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 140 ? '#111111' : '#ffffff';
}

// Content already fetched in this page load, keyed by request URL (which
// carries the settings revision), and requests still in flight. Both live for
// the page's lifetime only, so a full reload always asks again.
const contentCache = new Map();
const pending = new Map();

/**
 * Builds the Markdown widget class.
 *
 * The fof Widget base class is passed in (resolved at initializer time, not at
 * module load) so the class isn't defined against an `ext:` binding that may
 * not be registered yet during cross-bundle load -- which previously left the
 * component undefined and crashed fof's renderer.
 *
 * - marked + DOMPurify are bundled into the build (no CDN at runtime).
 * - Rendered HTML is always sanitised by DOMPurify before reaching m.trust().
 * - Content is fetched on demand when the widget mounts, so it is not
 *   serialised into the forum payload on every request.
 */
export default function makeMarkdownWidget(Widget) {
  return class MarkdownWidget extends Widget {
    oninit(vnode) {
      super.oninit(vnode);
      this.loading = true;
      this.data = {
        title: '',
        description: '',
        icon: '',
        body: '',
        backgroundColor: ''
      };

      // Cache-bust with the settings revision from the boot payload: when an
      // admin edits the widget the hash changes, so the URL changes and a normal
      // reload fetches fresh content instead of the browser's cached copy. When
      // nothing changed the URL is stable and the cached response is reused.
      const rev = app.data && app.data['linkrobins-markdown-widget.rev'] || '';
      const base = app.forum.attribute('apiUrl') + '/linkrobins-markdown-widget';
      const url = rev ? base + '?v=' + encodeURIComponent(rev) : base;

      // The widget area is rebuilt on every page change, which creates a new
      // instance of this component. Reuse the content an earlier instance
      // already loaded, so the widget appears in the same frame instead of
      // vanishing until a request (even one the browser answers from cache)
      // comes back, which shifted the page layout on every navigation.
      if (contentCache.has(url)) {
        this.data = contentCache.get(url);
        this.loading = false;
        return;
      }
      if (!pending.has(url)) {
        pending.set(url, app.request({
          method: 'GET',
          url
        }).then(data => {
          if (data) contentCache.set(url, data);
          return data;
        }).finally(() => pending.delete(url)));
      }
      pending.get(url).then(data => {
        if (data) this.data = data;
        this.loading = false;
        m.redraw();
      }).catch(e => {
        this.loading = false;
        console.error('[linkrobins/markdown-widget] failed to load content:', e);
        m.redraw();
      });
    }
    view(vnode) {
      // Don't render the widget shell until the content has loaded, to avoid a
      // flash of an empty widget while the request is in flight.
      if (this.loading) return null;
      const node = super.view(vnode);

      // Optional admin background color, applied as CSS custom properties the
      // widget card's content box derives its background and (auto-contrasting)
      // text color from (see forum.less). The regex is a guard since this lands
      // in an inline style; the fof root vnode carries our LinkRobinsMarkdownWidget
      // class, so the properties inherit down to .FofWidgets-Widget-content.
      const bg = this.data.backgroundColor || '';
      if (node && node.attrs && /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(bg)) {
        const fg = readableTextColor(bg);
        // Code and links follow the contrasting text color too: the theme's
        // code background and link color are picked for the theme's own
        // background, so on a dark admin color they left inline code as white
        // on near-white and links hard to read. Links get an underline because
        // they now share the text color.
        const vars = {
          '--lrmw-bg': bg,
          '--lrmw-fg': fg,
          '--lrmw-code-bg': fg === '#ffffff' ? 'rgba(255, 255, 255, 0.16)' : 'rgba(0, 0, 0, 0.08)',
          '--lrmw-link': fg,
          '--lrmw-link-decoration': 'underline'
        };
        const decl = Object.keys(vars).map(key => key + ': ' + vars[key]).join('; ');
        const style = node.attrs.style;
        if (style == null) node.attrs.style = decl;else if (typeof style === 'string') node.attrs.style = style.replace(/;\s*$/, '') + '; ' + decl;else Object.assign(style, vars);
      }
      return node;
    }
    className() {
      return 'LinkRobinsMarkdownWidget';
    }
    icon() {
      return this.data.icon || '';
    }
    title() {
      return this.data.title || '';
    }
    description() {
      return this.data.description || '';
    }

    // The base header() renders icon, title and description together, but only
    // when title() is set. Keep it as the primary path so that markup stays
    // fof's own, and hand-build the no-title case so a description (with the
    // icon, if one is set) still gets a header instead of rendering nothing.
    header() {
      const base = super.header();
      if (base) return base;
      const iconName = this.icon();
      const description = this.description();
      if (!description) return null;
      return m('div', {
        className: 'FofWidgets-Widget-title'
      }, iconName ? m('span', {
        className: 'FofWidgets-Widget-title-icon'
      }, m('i', {
        className: iconName
      })) : null, m('div', {
        className: 'FofWidgets-Widget-title-desc'
      }, description));
    }
    content() {
      const body = this.data.body || '';
      if (!body) return null;
      return m.trust(this.renderBody(body));
    }
    renderBody(body) {
      // Parsing markdown and sanitising run on every redraw, so memoise them:
      // the body only changes when an admin edits it, and re-parsing identical
      // markdown each redraw is wasted work.
      if (this._bodyCache !== body) {
        try {
          this._bodyHtml = DOMPurify.sanitize(marked.parse(body));
        } catch (e) {
          console.error('[linkrobins/markdown-widget] render failed:', e);
          this._bodyHtml = '<pre>' + this.escape(body) + '</pre>';
        }
        this._bodyCache = body;
      }
      return this._bodyHtml;
    }
    escape(s) {
      return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }
  };
}
flarum.reg.add('linkrobins-markdown-widget', 'common/components/makeMarkdownWidget', makeMarkdownWidget);